import { useState, useCallback } from "react";
import type { Product, GlowPost } from "@/lib/api";
import {
  adminFetchProducts,
  adminCreateProduct,
  adminUpdateProduct,
  adminDeleteProduct,
  adminFetchEditorial,
  adminUpdateEditorial,
} from "@/lib/api";

export interface AdminProduct extends Product {
  isActive: boolean;
}

export interface AdminPost extends GlowPost {
  isActive: boolean;
}

export function useAdminStore() {
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [posts, setPosts] = useState<AdminPost[]>([]);
  const [loading, setLoading] = useState(false);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminFetchProducts();
      setProducts(data.map((p) => ({ ...p, isActive: p.isActive ?? true })));
    } catch (err) {
      console.error("Failed to load products:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPosts = useCallback(async () => {
    try {
      const data = await adminFetchEditorial();
      setPosts(data.map((p) => ({ ...p, isActive: p.isActive ?? true })));
    } catch (err) {
      console.error("Failed to load posts:", err);
    }
  }, []);

  const toggleProduct = useCallback(async (id: string, isActive: boolean) => {
    try {
      await adminUpdateProduct(id, { isActive } as Partial<Product>);
      setProducts((prev) =>
        prev.map((p) => (p.id === id ? { ...p, isActive } : p))
      );
    } catch (err) {
      console.error("Failed to toggle product:", err);
    }
  }, []);

  const updateProduct = useCallback(async (id: string, fields: Partial<AdminProduct>) => {
    try {
      const updated = await adminUpdateProduct(id, fields);
      setProducts((prev) =>
        prev.map((p) => (p.id === id ? { ...p, ...updated, isActive: updated.isActive ?? p.isActive } : p))
      );
    } catch (err) {
      console.error("Failed to update product:", err);
    }
  }, []);

  const addProduct = useCallback(async (product: Omit<Product, "id" | "rating" | "reviewCount">) => {
    try {
      const created = await adminCreateProduct(product);
      setProducts((prev) => [{ ...created, isActive: true }, ...prev]);
    } catch (err) {
      console.error("Failed to add product:", err);
    }
  }, []);

  const deleteProduct = useCallback(async (id: string) => {
    try {
      await adminDeleteProduct(id);
      setProducts((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error("Failed to delete product:", err);
    }
  }, []);

  const togglePost = useCallback(async (id: string, isActive: boolean) => {
    try {
      await adminUpdateEditorial(id, { isActive } as Partial<GlowPost>);
      setPosts((prev) =>
        prev.map((p) => (p.id === id ? { ...p, isActive } : p))
      );
    } catch (err) {
      console.error("Failed to toggle post:", err);
    }
  }, []);

  return {
    products,
    posts,
    loading,
    loadProducts,
    loadPosts,
    toggleProduct,
    updateProduct,
    addProduct,
    deleteProduct,
    togglePost,
  };
}
